import config from '../config';
import Logger from '../logger';
import event from '../mixins/event';
import KeenSlider from 'keen-slider';

class ThemeTextStackAndCarousel {

	// Vars
	// ▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒▒

	/**
	 * Class constructor
	 */
	constructor(dom) {

		this.NAME = 'ThemeTextStackAndCarousel';

		this.dom = dom;
		this.carouselDesktopDom = this.dom.querySelector('[data-theme-text-stack-and-carousel-carousel-desktop]');
		this.carouselMobileDom = this.dom.querySelector('[data-theme-text-stack-and-carousel-carousel-mobile]');
		this.listItemButtons = this.dom.querySelectorAll('[data-theme-text-stack-and-carousel-list-item-button]');
		this.listItemButtonsActiveClass = 'theme-text-stack-and-carousel__list-item-button--active';

		this.desktopSlider = null;
		this.mobileSlider = null;
		this.activeSlider = null;

		this.handleSlideChanged = this.handleSlideChanged.bind(this);
		this.handleListItemButtonClick = this.handleListItemButtonClick.bind(this);
		this.handleMediaQueryChange = this.handleMediaQueryChange.bind(this);

		this.tabletMediaQuery = typeof window !== 'undefined'
			? window.matchMedia('(min-width: 720px)')
			: null;

		// Respect the OS-level "reduce motion" accessibility setting - an
		// auto-advancing carousel is exactly the kind of continuous motion
		// that setting exists to suppress.
		this.prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia
			? window.matchMedia('(prefers-reduced-motion: reduce)').matches
			: false;

		this.autoplayIntervalMs = 5000;
	}

	/**
	 * init - Initialize the component
	 **/
	init() {
		Logger.info(`${this.NAME} > init`);
		this.initCarousel();
	}

	/**
	 * initCarousel - Initialize the carousel
	 */
	initCarousel() {
		Logger.info(`${this.NAME} > initCarousel`);
		if (!this.carouselDesktopDom && !this.carouselMobileDom) return;

		this.bindListItemButtonClicks();
		this.initCarouselForCurrentViewport();

		if (this.tabletMediaQuery) {
			if (this.tabletMediaQuery.addEventListener) {
				this.tabletMediaQuery.addEventListener('change', this.handleMediaQueryChange);
			} else if (this.tabletMediaQuery.addListener) {
				this.tabletMediaQuery.addListener(this.handleMediaQueryChange);
			}
		}
	}

	bindListItemButtonClicks() {
		this.listItemButtons.forEach(button => {
			button.removeEventListener('click', this.handleListItemButtonClick);
			button.addEventListener('click', this.handleListItemButtonClick);
		});
	}

	handleListItemButtonClick(event) {
		const button = event.currentTarget;
		const idx = button.dataset.themeTextStackAndCarouselListItemButtonIdx;
		Logger.info(`${this.NAME} > listItemButtonClick > idx: ${idx}`);

		const slider = this.getActiveSlider();
		if (slider) {
			slider.moveToIdx(idx * 1);
		}
	}

	initCarouselForCurrentViewport() {
		if (this.isDesktopViewport()) {
			this.destroyMobileCarousel();
			this.initDesktopCarousel();
		} else {
			this.destroyDesktopCarousel();
			this.initMobileCarousel();
		}
	}

	isDesktopViewport() {
		if (this.tabletMediaQuery) {
			return this.tabletMediaQuery.matches;
		}

		if (typeof window !== 'undefined') {
			return window.innerWidth >= 720;
		}

		return false;
	}

	initDesktopCarousel() {
		if (!this.carouselDesktopDom || this.desktopSlider) return;

		this.desktopSlider = new KeenSlider(
			this.carouselDesktopDom,
			{
				loop: true,
				selector: '[data-theme-text-stack-and-carousel-carousel-item]',
				slides: {
					perView: 1,
				}
			},
			this.getAutoplayPlugins()
		);

		this.desktopSlider.on('slideChanged', this.handleSlideChanged);
		this.activeSlider = this.desktopSlider;
		const desktopIdx = this.desktopSlider.track && this.desktopSlider.track.details ? this.desktopSlider.track.details.rel : 0;
		this.updateActiveButton(desktopIdx);
	}

	initMobileCarousel() {
		if (!this.carouselMobileDom || this.mobileSlider) return;

		this.mobileSlider = new KeenSlider(
			this.carouselMobileDom,
			{
				loop: true,
				selector: '[data-theme-text-stack-and-carousel-carousel-item]',
				slides: {
					perView: 1,
				}
			},
			this.getAutoplayPlugins()
		);

		this.mobileSlider.on('slideChanged', this.handleSlideChanged);
		this.activeSlider = this.mobileSlider;
		const mobileIdx = this.mobileSlider.track && this.mobileSlider.track.details ? this.mobileSlider.track.details.rel : 0;
		this.updateActiveButton(mobileIdx);
	}

	/**
	 * getAutoplayPlugins - the plugin array to hand to `new KeenSlider()`.
	 * Skips autoplay entirely (returns []) under prefers-reduced-motion,
	 * rather than initializing it and immediately pausing it, so no timer
	 * is ever created for a visitor who asked for reduced motion.
	 */
	getAutoplayPlugins() {
		if (this.prefersReducedMotion) return [];
		return [this.createAutoplayPlugin(this.autoplayIntervalMs)];
	}

	/**
	 * createAutoplayPlugin - keen-slider plugin factory implementing the
	 * library's documented autoplay pattern: a self-rescheduling timeout
	 * that calls slider.next() (wraps via `loop: true` above), paused
	 * while the pointer/touch is over the slider and while the visitor is
	 * actively dragging a slide, and torn down when the slider is
	 * destroyed (e.g. on viewport change between mobile/desktop sliders).
	 */
	createAutoplayPlugin(intervalMs) {
		return (slider) => {
			let timeout;
			let mouseOver = false;

			const clearNextTimeout = () => clearTimeout(timeout);

			const nextTimeout = () => {
				clearTimeout(timeout);
				if (mouseOver) return;
				timeout = setTimeout(() => {
					slider.next();
				}, intervalMs);
			};

			const handlePointerOver = () => {
				mouseOver = true;
				clearNextTimeout();
			};

			const handlePointerOut = () => {
				mouseOver = false;
				nextTimeout();
			};

			slider.on('created', () => {
				slider.container.addEventListener('mouseover', handlePointerOver);
				slider.container.addEventListener('mouseout', handlePointerOut);
				slider.container.addEventListener('touchstart', handlePointerOver, { passive: true });
				slider.container.addEventListener('touchend', handlePointerOut, { passive: true });
				nextTimeout();
			});

			slider.on('dragStarted', clearNextTimeout);
			slider.on('animationEnded', nextTimeout);
			slider.on('updated', nextTimeout);

			slider.on('destroyed', () => {
				clearNextTimeout();
				slider.container.removeEventListener('mouseover', handlePointerOver);
				slider.container.removeEventListener('mouseout', handlePointerOut);
				slider.container.removeEventListener('touchstart', handlePointerOver);
				slider.container.removeEventListener('touchend', handlePointerOut);
			});
		};
	}

	destroyDesktopCarousel() {
		if (!this.desktopSlider) return;

		const slider = this.desktopSlider;
		slider.destroy();
		if (this.activeSlider === slider) {
			this.activeSlider = null;
		}

		this.desktopSlider = null;
	}

	destroyMobileCarousel() {
		if (!this.mobileSlider) return;

		const slider = this.mobileSlider;
		slider.destroy();
		if (this.activeSlider === slider) {
			this.activeSlider = null;
		}

		this.mobileSlider = null;
	}

	getActiveSlider() {
		if (this.isDesktopViewport()) {
			return this.desktopSlider;
		}
		return this.mobileSlider;
	}

	updateActiveButton(idx) {
		if (!this.listItemButtons || !this.listItemButtons.length) return;

		this.listItemButtons.forEach(button => {
			button.classList.remove(this.listItemButtonsActiveClass);
		});

		const listItemButton = this.listItemButtons[idx];

		if (listItemButton) {
			listItemButton.classList.add(this.listItemButtonsActiveClass);
		}
	}

	handleSlideChanged(slider) {
		const idx = slider.track && slider.track.details ? slider.track.details.rel : 0;
		Logger.info(`${this.NAME} > slideChanged > idx: ${idx}`);
		this.updateActiveButton(idx);
	}

	handleMediaQueryChange() {
		this.initCarouselForCurrentViewport();
		this.activeSlider = this.getActiveSlider();
		if (this.activeSlider) {
			const idx = this.activeSlider.track && this.activeSlider.track.details ? this.activeSlider.track.details.rel : 0;
			this.updateActiveButton(idx);
		}
	}
}

Object.assign(ThemeTextStackAndCarousel.prototype, event);

export default ThemeTextStackAndCarousel;
